// Price and forecast history derived from market entities, for the charts that draw them. Kept apart from
// marketEntityTransform so surfaces without charts (market tiles, the phone home page) don't load the sampling
// math or the forecast-extrapolation worker client.
import Decimal from 'decimal.js';
import { DateTime } from 'luxon';

import type { PERIOD_TYPE } from 'constants/charting';
import type {
    HistoricalDataPeriodInterval,
    HistoricalMarketCandlestickStats,
    MarketCandlestickVo,
    MarketStatsType,
} from 'models/historical_data';
import type { HistoricalMarketStats } from 'models/stats';
import { linspace, makeMembersCallable } from 'utils/arrays';
import { candlestickCountContracts } from 'utils/candlestickHistoryTransform';
import {
    calculateStartTs,
    getCandlestickSamplingPointsNumber,
    getLineSamplingPointsNumber,
    periodIntervalToSeconds,
} from 'utils/charting';
import type { NumericTargetInput } from 'utils/numericTarget';

import {
    type MarketEntity,
    type EventEntity,
    isMarketDetermined,
    eventHasOpenMarkets,
    eventIsParsableNumeric,
    eventNumericallySortedMarkets,
    numericLadder,
    bucketsFromLadder,
    eventOpenDate,
    eventCloseDate,
} from './marketEntityTransform';

/** The OHLC shape `sampledHistory('candlesticks')` needs, preserving fractional counts. */
export const mapCandlesticksToCandlesticksStatsHistory = (
    candlesticks: MarketCandlestickVo[],
    periodInterval: HistoricalDataPeriodInterval,
): HistoricalMarketCandlestickStats[] => {
    let accumulatedVolume = new Decimal(0);
    let lastNonZeroPriceCandlestick = candlesticks[0];
    const periodIntervalInSeconds = periodIntervalToSeconds(periodInterval);

    return candlesticks.map((candlestick) => {
        accumulatedVolume = accumulatedVolume.plus(
            candlestickCountContracts(candlestick.volume_fp, candlestick.volume),
        );
        if (candlestick.price.close > 0) {
            lastNonZeroPriceCandlestick = candlestick;
        }

        return {
            yesAsk: lastNonZeroPriceCandlestick.yes_ask,
            yesBid: lastNonZeroPriceCandlestick.yes_bid,
            price: lastNonZeroPriceCandlestick.price,
            volume: accumulatedVolume.toNumber(),
            openInterest: candlestickCountContracts(candlestick.open_interest_fp, candlestick.open_interest).toNumber(),
            startTs: candlestick.end_period_ts - periodIntervalInSeconds,
            ts: candlestick.end_period_ts - periodIntervalInSeconds,
        };
    });
};

/**
 * Mirrors `BinaryMarket.extrapolateStatsHistory` — fills timestamp gaps in `statsHistory` by
 * repeating the prior point at each missing `periodInterval` boundary. Only the STATS (line)
 * ladder is extrapolated this way; `candlestickStatsHistory` is used as-is (gap-filling for
 * candlesticks happens later, in `boundedSampledHistory`'s doji-repeat sampling step).
 */
export const extrapolateStatsHistory = (
    statsHistory: HistoricalMarketStats[],
    periodInterval: HistoricalDataPeriodInterval,
): HistoricalMarketStats[] => {
    if (statsHistory.length === 0) return statsHistory;

    const extrapolatedArray: HistoricalMarketStats[] = [];
    const periodIntervalInSeconds = periodIntervalToSeconds(periodInterval);
    const lastTs = statsHistory[statsHistory.length - 1].ts;

    let currentTs = statsHistory[0].ts;
    let currentIndex = 0;

    while (currentTs <= lastTs) {
        const currentItem = statsHistory[currentIndex];
        extrapolatedArray.push(currentItem);
        currentTs = currentItem.ts;

        if (currentIndex === statsHistory.length - 1) {
            break;
        }
        const nextItem = statsHistory[currentIndex + 1];

        while (nextItem && nextItem.ts > currentTs + periodIntervalInSeconds) {
            extrapolatedArray.push({ ...currentItem, ts: currentTs + periodIntervalInSeconds });
            currentTs += periodIntervalInSeconds;
        }

        currentIndex++;
    }

    return extrapolatedArray;
};

const INITIAL_STATS: HistoricalMarketStats = {
    yesAsk: 50,
    yesBid: 50,
    price: 0,
    volume: 0,
    openInterest: 0,
    ts: 0,
};
/**
 * Mirrors `BinaryMarket`'s module-level `INITIAL_CANDLESTICK_STATS` — including its
 * `startTs`/`ts` quirk: `boundedSampledHistory`'s `updateSamplingFromDataPoint` never
 * overwrites `startTs` on the scratch "sampling" object, so any synthesized/doji-repeated
 * point produced before the FIRST real data point in the window keeps whatever `startTs`
 * this constant was built with — frozen at module-load time in the original class (a
 * `Date.now()` computed once and reused forever), never read by any chart consumer.
 * Hardcoded to `0` here rather than `Date.now()` for determinism — same harmless-garbage
 * behavior, just a fixed placeholder instead of a frozen wall-clock moment.
 */
const INITIAL_CANDLESTICK_STATS: HistoricalMarketCandlestickStats = {
    yesAsk: { open: 50, close: 50, high: 50, low: 50, mean: 50 },
    yesBid: { open: 50, close: 50, high: 50, low: 50, mean: 50 },
    price: { open: 0, close: 0, high: 0, low: 0, mean: 0 },
    volume: 0,
    openInterest: 0,
    startTs: 0,
    ts: 0,
};

/** Mirrors `BinaryMarket.emptyStatsHistory`/`emptyCandlestickStatsHistory` — the placeholder a market's history defaults to before its first fetch resolves. Never truly empty (`boundedSampledHistory` assumes a first element exists). */
export const emptyCandlestickStatsHistory = (
    now: number = Math.round(Date.now() / 1000),
): HistoricalMarketCandlestickStats[] => [{ ...INITIAL_CANDLESTICK_STATS, startTs: now, ts: now }];

/**
 * Mirrors `BinaryMarket.boundedSampledHistory` — resamples raw history into `sampleSize` evenly
 * spaced points across `[start, end]`, repeating the last known point (as a flat doji for
 * candlesticks) to fill any gap where the underlying data polls less often than the sample rate.
 */
export function boundedSampledHistory(
    data: HistoricalMarketStats[],
    start: number,
    end: number,
    sampleSize: number,
    type?: 'stats',
    hideOffHours?: boolean,
    backfillFirstPrice?: boolean,
): HistoricalMarketStats[];
export function boundedSampledHistory(
    data: HistoricalMarketCandlestickStats[],
    start: number,
    end: number,
    sampleSize: number,
    type: 'candlesticks',
    hideOffHours?: boolean,
    backfillFirstPrice?: boolean,
): HistoricalMarketCandlestickStats[];
export function boundedSampledHistory(
    data: HistoricalMarketStats[] | HistoricalMarketCandlestickStats[],
    start: number,
    end: number,
    sampleSize: number,
    type: MarketStatsType = 'stats',
    // Accepted for signature parity with `BinaryMarket.boundedSampledHistory` — unused there too.
    _hideOffHours = false,
    backfillFirstPrice = false,
): HistoricalMarketStats[] | HistoricalMarketCandlestickStats[] {
    const pointIsCandlestick = (
        point: HistoricalMarketStats | HistoricalMarketCandlestickStats,
    ): point is HistoricalMarketCandlestickStats => !!point && type === 'candlesticks';
    const repeatCandleStickAsDoji = (
        candlestick: HistoricalMarketCandlestickStats,
    ): HistoricalMarketCandlestickStats => {
        const repeatedCandlestick = JSON.parse(JSON.stringify(candlestick)) as HistoricalMarketCandlestickStats;
        const { price, yesAsk, yesBid } = candlestick;
        repeatedCandlestick.price = { ...price, open: price.close, high: price.close, low: price.close };
        repeatedCandlestick.yesAsk = { ...yesAsk, open: yesAsk.close, high: yesAsk.close, low: yesAsk.close };
        repeatedCandlestick.yesBid = { ...yesBid, open: yesBid.close, high: yesBid.close, low: yesBid.close };
        return repeatedCandlestick;
    };

    const updateSamplingFromDataPoint = (
        sampling: HistoricalMarketStats | HistoricalMarketCandlestickStats,
        dataPoint: HistoricalMarketStats | HistoricalMarketCandlestickStats,
    ): void => {
        sampling.yesAsk = dataPoint.yesAsk;
        sampling.yesBid = dataPoint.yesBid;
        sampling.openInterest = dataPoint.openInterest;
        sampling.price = dataPoint.price;
        sampling.volume = dataPoint.volume;
    };

    const windowSize = end - start + 1;
    const samplingRate = windowSize / sampleSize;
    let expandedData: HistoricalMarketStats[] | HistoricalMarketCandlestickStats[] = [];
    const sampling: HistoricalMarketStats | HistoricalMarketCandlestickStats = {
        ...(type === 'stats' ? INITIAL_STATS : INITIAL_CANDLESTICK_STATS),
        ts: start,
    };

    // volume to deduct from sampled points.
    let usedVolume = start >= data[0].ts ? data[0]?.volume : 0;
    for (let i = 0; i < data.length && data[i].ts < end; i++) {
        // if data rate is low, collect multiple samples for single datapoint
        let isFirstSampleForDataPoint = true;
        while (sampling.ts <= data[i].ts) {
            const currentVolume = sampling.volume - usedVolume;

            if (pointIsCandlestick(sampling)) {
                const repeatedCandlestick = isFirstSampleForDataPoint ? sampling : repeatCandleStickAsDoji(sampling);
                (expandedData as HistoricalMarketCandlestickStats[]).push({
                    ...repeatedCandlestick,
                    volume: currentVolume,
                });
            } else {
                (expandedData as HistoricalMarketStats[]).push({ ...sampling, volume: currentVolume });
            }
            sampling.ts += samplingRate;
            usedVolume += currentVolume;
            isFirstSampleForDataPoint = false;
        }
        updateSamplingFromDataPoint(sampling, data[i]);

        if (data[i].ts <= start) {
            usedVolume = data[i].volume;
        }
    }

    if (data.length > 0) {
        updateSamplingFromDataPoint(sampling, data[data.length - 1]);
    }
    // if needed, collect more samples after by repeating last available datapoint
    let isFirstSampleForDataPoint = true;
    while (sampling.ts < end + samplingRate) {
        const currentVolume = sampling.volume - usedVolume;
        if (pointIsCandlestick(sampling)) {
            const repeatedCandlestick = isFirstSampleForDataPoint ? sampling : repeatCandleStickAsDoji(sampling);
            (expandedData as HistoricalMarketCandlestickStats[]).push({
                ...repeatedCandlestick,
                volume: currentVolume,
            });
        } else {
            (expandedData as HistoricalMarketStats[]).push({ ...sampling, volume: currentVolume });
        }
        sampling.ts += samplingRate;
        usedVolume += currentVolume;
        isFirstSampleForDataPoint = false;
    }

    // Filter out points with no price, volume, or open interest
    const filteredData = makeMembersCallable(expandedData).filter((stat) =>
        pointIsCandlestick(stat) ? !!stat.price.open || !!stat.volume : !!stat.price || !!stat.volume,
    );

    // Backfill first price if needed
    if (backfillFirstPrice && filteredData.length > 0 && type === 'stats') {
        const firstPrice = (filteredData[0] as HistoricalMarketStats).price;
        expandedData = makeMembersCallable(expandedData).map((stat) => ({
            ...stat,
            price: (stat as HistoricalMarketStats).price === 0 ? firstPrice : (stat as HistoricalMarketStats).price,
        })) as HistoricalMarketStats[];
    } else {
        expandedData = makeMembersCallable(filteredData) as
            | HistoricalMarketStats[]
            | HistoricalMarketCandlestickStats[];
    }

    return expandedData;
}

/**
 * Mirrors `BinaryMarket.sampledHistory` — wraps `boundedSampledHistory` with the market's own
 * open/close window and appends a settlement data point for determined markets (so the chart
 * ends at the resolution price instead of the last traded price). `now` defaults to the
 * call-time clock (no reactive timer — recompute on your own cadence, same as `isMarketClosed`).
 */
export function marketSampledHistory(
    market: MarketEntity,
    statsHistory: HistoricalMarketStats[],
    candlestickStatsHistory: HistoricalMarketCandlestickStats[],
    period: PERIOD_TYPE,
    type: 'candlesticks',
    setSampleSize?: number,
    now?: number,
): HistoricalMarketCandlestickStats[];
export function marketSampledHistory(
    market: MarketEntity,
    statsHistory: HistoricalMarketStats[],
    candlestickStatsHistory: HistoricalMarketCandlestickStats[],
    period: PERIOD_TYPE,
    type?: 'stats',
    setSampleSize?: number,
    now?: number,
): HistoricalMarketStats[];
export function marketSampledHistory(
    market: MarketEntity,
    statsHistory: HistoricalMarketStats[],
    candlestickStatsHistory: HistoricalMarketCandlestickStats[],
    period: PERIOD_TYPE,
    type: MarketStatsType = 'stats',
    setSampleSize: number | undefined = undefined,
    now: number = Math.round(Date.now() / 1000),
): HistoricalMarketStats[] | HistoricalMarketCandlestickStats[] {
    const marketOpenTs = Math.round(new Date(market.openDate).getTime() / 1000);
    const marketCloseTs = Math.round(new Date(market.closeDate).getTime() / 1000);

    const end = Math.min(marketCloseTs, now);
    const start = calculateStartTs(end, marketOpenTs, period);

    const sampleSize = !!setSampleSize
        ? setSampleSize
        : type === 'candlesticks'
          ? getCandlestickSamplingPointsNumber()
          : getLineSamplingPointsNumber(period);

    const data =
        type === 'candlesticks'
            ? boundedSampledHistory(candlestickStatsHistory, start, end, sampleSize, 'candlesticks')
            : boundedSampledHistory(statsHistory, start, end, sampleSize);

    if (isMarketDetermined(market) && data.length > 0 && !!market.result) {
        const settlementPrice = market.settlementValueCents;
        const lastPoint = data[data.length - 1];
        const settlementTs = lastPoint.ts + 1;

        if (type === 'candlesticks') {
            const ohlc = {
                open: settlementPrice,
                close: settlementPrice,
                high: settlementPrice,
                low: settlementPrice,
                mean: settlementPrice,
            };
            (data as HistoricalMarketCandlestickStats[]).push({
                yesAsk: ohlc,
                yesBid: ohlc,
                price: ohlc,
                volume: 0,
                openInterest: 0,
                startTs: settlementTs,
                ts: settlementTs,
            });
        } else {
            (data as HistoricalMarketStats[]).push({
                yesAsk: settlementPrice,
                yesBid: settlementPrice,
                price: settlementPrice,
                volume: 0,
                openInterest: 0,
                ts: settlementTs,
            });
        }
    }

    return data;
}

/** Mirrors `BinaryMarket.lastBidAskBeforeTime`. */
export const marketLastBidAskBeforeTime = (statsHistory: HistoricalMarketStats[], date: Date): [number, number] => {
    const ts = date.getTime() / 1000;
    if (statsHistory.length === 0 || statsHistory[0].ts > ts) return [0, 100];

    const beforeStats = statsHistory.filter((s) => s.ts < ts);
    const stat = beforeStats[beforeStats.length - 1];
    if (stat === undefined) return [0, 100];

    return [stat.yesBid, stat.yesAsk];
};

/** Each market's last quote strictly before `date`, in the order of `sortedMarkets`. */
const eventBidsAndAsksAtTime = (
    sortedMarkets: MarketEntity[],
    statsHistoryByTicker: Record<string, HistoricalMarketStats[]>,
    date: Date,
): [number[], number[]] => {
    const bids: number[] = [];
    const asks: number[] = [];
    for (const market of sortedMarkets) {
        const [bid, ask] = marketLastBidAskBeforeTime(statsHistoryByTicker[market.ticker] ?? [], date);
        bids.push(bid);
        asks.push(ask);
    }
    return [bids, asks];
};

/**
 * Builds the `[bucket count, forecast]` estimate `eventSmoothedHistoricalMeans` takes at each sample and at the
 * live point. It is built once per history from `eventNumericallySortedMarkets` output, and every sample's quotes
 * arrive in that order. A zero count marks the sample illiquid, so the previous forecast carries forward.
 */
export type NumericForecastEstimator = (
    sortedMarkets: NumericTargetInput[],
) => (bids: number[], asks: number[]) => [number, number];

/** Mirrors `Event.bucketCountAndMean`, the bucket-weighted mean. `eventSmoothedHistoricalMeans`' default estimator. */
export const eventBucketCountAndMean: NumericForecastEstimator = (sortedMarkets) => {
    const ladder = numericLadder(sortedMarkets);
    return (bids, asks) => {
        const buckets = bucketsFromLadder(ladder, bids, asks);
        const naiveEV = buckets.map((b) => b.midpoint * b.probability).reduce((partialSum, a) => partialSum + a, 0);
        return [
            buckets.length,
            naiveEV / buckets.map((b) => b.probability).reduce((partialSum, a) => partialSum + a, 0.00001),
        ];
    };
};

/**
 * `eventCloseDate`, but with the removed `Event.fixNflDate` correction re-applied — for
 * `2025`-dated `KXNFLGAME*` events whose close date comes back from the backend two years ahead
 * (`2027` instead of `2025`), rewrites it back rather than requesting/rendering a two-year-long
 * chart range. https://kalshi.slack.com/archives/CSHRA7JLD/p1757605510543909
 *
 * `eventCloseDate` deliberately skips this (see its own doc comment) since it's irrelevant
 * outside this one series — use that for every other consumer. This variant exists solely for
 * `MarketPriceHistoryGraphWrapper`'s forecast-history range, which mirrors the removed
 * `BinaryMarket.loadMarketForecastHistory`'s use of `parentEvent.closeDate` (the getter that
 * included the fix).
 */
export const eventCloseDateForForecastHistory = (
    event: Pick<EventEntity, 'ticker' | 'targetDatetime'>,
    markets: MarketEntity[],
): string => {
    const closeDate = eventCloseDate(markets);
    if (
        !closeDate ||
        !event.ticker.startsWith('KXNFLGAME') ||
        !event.targetDatetime.startsWith('2025') ||
        !closeDate.startsWith('2027')
    ) {
        return closeDate;
    }
    return DateTime.fromISO(closeDate.replace('2027', '2025')).plus({ hours: 12 }).toISO() ?? closeDate;
};

/**
 * Mirrors `Event.smoothedHistoricalMeans`. `allCandlesticksLoaded` replaces the original getter's
 * `this.markets.some((m) => !m.isCandlesticksLoaded)` guard — the caller (a React Query hook,
 * not a store loading flag) knows whether every market's candlestick fetch has resolved.
 * `estimator` replaces the bucket-weighted mean at every sample and the live point; margin
 * forecasts use it to plot the market median instead.
 */
export const eventSmoothedHistoricalMeans = (
    markets: MarketEntity[],
    statsHistoryByTicker: Record<string, HistoricalMarketStats[]>,
    allCandlesticksLoaded: boolean,
    numPoints = 200,
    estimator: NumericForecastEstimator = eventBucketCountAndMean,
): [Date[], number[]] => {
    if (!eventIsParsableNumeric(markets) || !allCandlesticksLoaded) return [[], []];

    // Accumulate min/max in one pass — spreading into Math.min/Math.max overflows the call
    // stack when the stats history has hundreds of thousands of entries.
    let minStatHistoryTime = Infinity;
    let maxStatHistoryTime = -Infinity;
    for (const market of markets) {
        for (const stat of statsHistoryByTicker[market.ticker] ?? []) {
            if (stat.ts < minStatHistoryTime) minStatHistoryTime = stat.ts;
            if (stat.ts > maxStatHistoryTime) maxStatHistoryTime = stat.ts;
        }
    }
    if (minStatHistoryTime === Infinity) return [[], []];

    const startTs = new Date(Math.max(new Date(eventOpenDate(markets)).getTime(), minStatHistoryTime * 1000 + 1000));
    const endTs = new Date(maxStatHistoryTime * 1000);

    // Cut out the first 20% to avoid awkward price discovery
    const laterStartTs = new Date((startTs.getTime() * 4 + endTs.getTime()) / 5);

    const allTimes = linspace(laterStartTs.getTime(), endTs.getTime(), numPoints).map((num) => new Date(num));

    const sortedMarkets = eventNumericallySortedMarkets(markets);
    const estimate = estimator(sortedMarkets);
    const allBucketCountsAndMeans = allTimes.map((pastDate): [number, number] =>
        estimate(...eventBidsAndAsksAtTime(sortedMarkets, statsHistoryByTicker, pastDate)),
    );

    // Pass liquid measurements forward in one pass
    for (let i = 1; i < allBucketCountsAndMeans.length; i++) {
        if (allBucketCountsAndMeans[i][0] === 0) {
            allBucketCountsAndMeans[i] = allBucketCountsAndMeans[i - 1];
        }
    }

    const liquidTimes: Date[] = [];
    const liquidBucketCountsAndMeans: [number, number][] = [];
    // Clear illiquid measurements from the start
    for (let i = 1; i < allBucketCountsAndMeans.length; i++) {
        if (allBucketCountsAndMeans[i][0] !== 0) {
            liquidBucketCountsAndMeans.push(allBucketCountsAndMeans[i]);
            liquidTimes.push(allTimes[i]);
        }
    }

    // Exit early if there's no data
    if (liquidBucketCountsAndMeans.length === 0) return [[], []];

    const adjustedTimes: Date[] = [];
    const means: number[] = [];

    // Merge adjacent entries that have different bucket acounts
    let ifShouldSkip = false;
    for (let i = 0; i < liquidTimes.length; i++) {
        if (ifShouldSkip) {
            ifShouldSkip = false;
            continue;
        }

        if (i === liquidTimes.length - 1 || liquidBucketCountsAndMeans[i][0] === liquidBucketCountsAndMeans[i + 1][0]) {
            adjustedTimes.push(liquidTimes[i]);
            means.push(liquidBucketCountsAndMeans[i][1]);
        } else {
            adjustedTimes.push(new Date((liquidTimes[i].getTime() + liquidTimes[i + 1].getTime()) / 2));
            means.push((liquidBucketCountsAndMeans[i][1] + liquidBucketCountsAndMeans[i + 1][1]) / 2);
        }
    }

    const currentTime = new Date();
    if (eventHasOpenMarkets(markets)) {
        const [bucketCount, current] = estimate(
            sortedMarkets.map((m) => m.yesBid),
            sortedMarkets.map((m) => m.yesAsk),
        );

        if (bucketCount !== 0) {
            means.push(current);
            adjustedTimes.push(currentTime);
        } else if (means.length > 0) {
            means.push(means.slice(-1)[0]);
            adjustedTimes.push(currentTime);
        }
    }

    return [adjustedTimes, means];
};
